import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { DogForm } from "@/components/DogForm";
import { DogCard } from "@/components/DogCard";
import { takePendingDog } from "@/components/QuizSaveDog";
import { HouseholdBundle } from "@/components/HouseholdBundle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/account/dogs`;
const opts = { withCredentials: true };

export const AccountDogs = ({ t, lang }) => {
  const { add } = useCart();
  const [dogs, setDogs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    axios.get(API, opts).then((r) => setDogs(Array.isArray(r.data) ? r.data : [])).catch(() => setDogs([]));
  }, []);
  useEffect(() => {
    const pending = takePendingDog();
    if (!pending) { load(); return; }
    axios.post(API, pending, opts)
      .then(() => setNotice(t(`${pending.name} is on the ledger now.`, `${pending.name} est maintenant au registre.`)))
      .catch(() => {})
      .finally(load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const save = async (body) => {
    setSaving(true); setError("");
    try {
      if (editing === "new") await axios.post(API, body, opts);
      else await axios.put(`${API}/${editing}`, body, opts);
      setEditing(null); load();
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("That didn't save. Please try again.", "L'enregistrement a échoué. Veuillez réessayer."));
    } finally { setSaving(false); }
  };
  const remove = async (dog) => {
    if (!window.confirm(t(`Remove ${dog.name} from your ledger?`, `Retirer ${dog.name} de votre registre?`))) return;
    await axios.delete(`${API}/${dog.dog_id}`, opts).catch(() => {});
    load();
  };
  const current = dogs?.find((d) => d.dog_id === editing);

  return (
    <section data-testid="account-dogs">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]/80">{t("Your dogs", "Vos chiens")}</h2>
        {dogs && dogs.length > 0 && dogs.length < 10 && editing === null && (
          <button onClick={() => { setError(""); setEditing("new"); }} data-testid="dog-add-button"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#B5A642] hover:text-[#E8DDC7] transition-colors">
            <Plus className="w-3.5 h-3.5" /> {t("Add another dog", "Ajouter un chien")}
          </button>
        )}
      </div>
      {notice && <p data-testid="dog-pending-saved" className="mt-4 font-display italic text-lg text-[#B5A642]">{notice}</p>}
      {dogs === null && <p className="mt-6 text-[#D3CEC4]/50">{t("Calling the dogs in…", "On appelle les chiens…")}</p>}
      {dogs && dogs.length === 0 && editing === null && (
        <div className="mt-6" data-testid="account-no-dogs">
          <p className="font-display italic text-2xl text-[#D3CEC4]/70">{t("No dogs on the ledger yet.", "Aucun chien au registre pour l'instant.")}</p>
          <p className="mt-3 text-[14px] leading-relaxed text-[#D3CEC4]/60 max-w-xl">{t("Tell us who they are and what they weigh. We'll match their jars and keep count of when each one runs low.", "Dites-nous qui ils sont et combien ils pèsent. Nous choisirons leurs pots et compterons quand chacun s'épuisera.")}</p>
          <button onClick={() => setEditing("new")} data-testid="dog-add-first"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#4B3226] text-[#E8DDC7] text-[11px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 transition-[filter] duration-300">
            <Plus className="w-3.5 h-3.5" /> {t("Introduce your dog", "Présentez votre chien")}
          </button>
        </div>
      )}
      {editing !== null && (
        <div className="mt-6">
          <DogForm key={editing} t={t} lang={lang} initial={current} onSave={save} onCancel={() => setEditing(null)} saving={saving} error={error} />
        </div>
      )}
      {dogs && dogs.length > 0 && (
        <ul className="mt-6 space-y-5">
          {dogs.filter((d) => d.dog_id !== editing).map((d) => (
            <DogCard key={d.dog_id} dog={d} t={t} lang={lang} onAdd={add}
              onEdit={() => { setError(""); setEditing(d.dog_id); }} onDelete={() => remove(d)} />
          ))}
        </ul>
      )}
      {dogs && dogs.length > 0 && <HouseholdBundle t={t} refreshKey={dogs} />}
      <p className="mt-5 text-[12px] text-[#D3CEC4]/50 max-w-xl">{t("Timings are estimated from your past orders and each dog's daily measure. When dogs share a jar, we count every one of them.", "Les délais sont estimés selon vos commandes passées et la mesure quotidienne de chaque chien. Quand des chiens partagent un pot, nous les comptons tous.")}</p>
    </section>
  );
};
